import { Tooltip, Typography } from "@mui/material";
import { FormattedDate } from "utils/DateUtils";
import useVoyageProgressBar from "../../hooks/useVoyageProgressBar";
import {
	ProgressContainer,
	ProgressBackground,
	ProgressFill,
	DotContainer,
	ActiveCallArrow,
	ProgressDot,
	VoyageBoundariesContainer,
	BoundaryInfo,
	TooltipContent,
	SailingIcon,
	AnchorIcon,
} from "./VoyageProgressBar.styles";
import { VoyageProgressBarVoyageFragment } from "__generated__/view/voyages/graphql/voyage.fragments.generated";

type VoyageProgressBarProps = {
    voyage: VoyageProgressBarVoyageFragment;
}
const VoyageProgressBar = ({ voyage }: VoyageProgressBarProps) => {
	const { points, activeCall, percentage, isEnRoute, allFinished, startPoint, endPoint } = useVoyageProgressBar(voyage);

	return (
		<>
			<ProgressContainer>
				<ProgressBackground>
					<ProgressFill percentage={percentage} />
				</ProgressBackground>

				{points.map((point, index) => {
					const position = (index / (points.length - 1)) * 100;
					const isAtActivePoint = activeCall?.id === point.callId;
					const isLastPoint = index === points.length - 1;
					const dotColor = point.isFinished ? "#F39200" : "#ddd";
					const isHighlighted = isLastPoint && allFinished;
					const isDotVisible = !(isAtActivePoint && !isEnRoute && !allFinished);

					return (
						<Tooltip
							title={(
								<TooltipContent>
									<b>{point.location?.description}</b>
									<span>
										{point.time.ata ? "ATA" : "PTA" }:{" "}
										<FormattedDate date={point.time.ata ?? point.time.pta} />
									</span>
									{point.time.atd && (
										<span>
											{"ATD" }:{" "}
											<FormattedDate date={point.time.atd} />
										</span>
									)}
								</TooltipContent>
							)}
							key={point.callId}
						>
							<DotContainer position={position} isHighlighted={isHighlighted}>
								<ProgressDot
									dotColor={dotColor}
									isVisible={isDotVisible}
									isHighlighted={isHighlighted}
								/>
							</DotContainer>
						</Tooltip>
					);
				})}

				{(isEnRoute || (!allFinished && activeCall)) && (
					<ActiveCallArrow percentage={percentage} hideCircle={percentage === 100}>
						{isEnRoute ? (
							<SailingIcon color="primary" />
						) : (
							<Tooltip
								title={activeCall ? (
									<TooltipContent>
										<b>{activeCall.location?.description}</b>
										<span>
											{activeCall.ata ? "ATA" : "PTA" }:{" "}
											<FormattedDate date={activeCall.ata ?? activeCall.pta} />
										</span>
										{activeCall.atd && (
											<span>
												{"ATD" }:{" "}
												<FormattedDate date={activeCall.atd} />
											</span>
										)}
									</TooltipContent>
								) : ""}
								disableHoverListener={!activeCall}
							>
								<AnchorIcon color="primary" />
							</Tooltip>
						)}
					</ActiveCallArrow>
				)}
			</ProgressContainer>

			<VoyageBoundariesContainer>
				<BoundaryInfo align="start" isFinished={startPoint.isFinished}>
					<Typography sx={{ fontSize: 13, fontWeight: 600 }} color="textPrimary">
						{startPoint.location?.description}
					</Typography>

					{!startPoint.isSimulated && (
						<small>
							<FormattedDate
								date={startPoint.time.atd ?? startPoint.time.ata ?? startPoint.time.pta}
								withoutYear
							/>
						</small>
					)}
				</BoundaryInfo>
				<BoundaryInfo align="end" isFinished={endPoint.isFinished}>
					<Typography sx={{ fontSize: 13, fontWeight: 600 }} color="textPrimary">
						{endPoint.location?.description}
					</Typography>

					<small>
						{!endPoint.isSimulated && (
							<FormattedDate
								date={endPoint.time.atd ?? endPoint.time.ata ?? endPoint.time.pta}
								withoutYear
							/>
						)}
					</small>
				</BoundaryInfo>
			</VoyageBoundariesContainer>
		</>
	);
};

export default VoyageProgressBar;
